<template>
    <div class="relative isolate overflow-hidden bg-gray-950 py-16 sm:py-24 lg:py-32">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
                <div class="max-w-xl lg:max-w-lg">
                    <h2 class="text-4xl font-semibold tracking-tight text-white">Join Our Community</h2>
                    <p class="mt-4 text-lg text-gray-300">Join us and experience an enriching journey through our
                        online Quran, Arabic, and Islamic studies programs with native Arabic tutors.</p>
                    <div class="mt-8">
                        <router-link to="/register">
                            <Button variant="primary" size="lg">Get Started</Button>
                        </router-link>
                    </div>
                </div>
                <dl class="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:pt-2">
                    <div class="flex flex-col items-start">
                        <div class="rounded-md bg-white/5 p-2 ring-1 ring-white/10">
                            <CalendarDaysIcon class="size-6 text-white" aria-hidden="true" />
                        </div>
                        <dt class="mt-4 text-base font-semibold text-white">Flexible Scheduling</dt>
                        <dd class="mt-2 text-base/7 text-gray-400">Choose your own schedule and learn at your own
                            pace, wherever you are.</dd>
                    </div>
                    <div class="flex flex-col items-start">
                        <div class="rounded-md bg-white/5 p-2 ring-1 ring-white/10">
                            <HandRaisedIcon class="size-6 text-white" aria-hidden="true" />
                        </div>
                        <dt class="mt-4 text-base font-semibold text-white">Native Arabic Tutors</dt>
                        <dd class="mt-2 text-base/7 text-gray-400">Learn from native Arabic speakers proficient in
                            English for an authentic experience.</dd>
                    </div>
                </dl>
            </div>
        </div>
    </div>

    <footer class="bg-gray-950 border-t border-white/10">
        <div class="mx-auto max-w-7xl px-6 py-16 lg:px-8">
            <div class="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                    <div class="inline-flex rounded-full bg-white p-1.5">
                        <img class="h-8 w-auto object-contain" src="/images/logo.png" alt="logo" />
                    </div>
                    <p class="mt-4 text-sm/6 text-gray-400">
                        Learn Arabic, Quran, and Islamic studies online with native, certified tutors — on a schedule
                        that fits your life.
                    </p>
                </div>

                <div>
                    <h3 class="text-sm font-semibold text-white">Quick Links</h3>
                    <ul role="list" class="mt-4 space-y-3">
                        <li v-for="link in quickLinks" :key="link.name">
                            <router-link :to="link.href" class="text-sm text-gray-400 hover:text-white">
                                {{ link.name }}
                            </router-link>
                        </li>
                    </ul>
                </div>

                <div>
                    <h3 class="text-sm font-semibold text-white">Programs</h3>
                    <ul role="list" class="mt-4 space-y-3">
                        <li v-for="category in categories" :key="category.slug">
                            <router-link :to="`/courses?category=${category.slug}`" class="text-sm text-gray-400 hover:text-white">
                                {{ category.name }}
                            </router-link>
                        </li>
                        <li v-if="!categories.length">
                            <router-link to="/courses" class="text-sm text-gray-400 hover:text-white">
                                Browse all courses
                            </router-link>
                        </li>
                    </ul>
                </div>

                <div>
                    <h3 class="text-sm font-semibold text-white">Follow Us</h3>
                    <div class="mt-4 flex items-center gap-3">
                        <a v-for="social in socials" :key="social.name" :href="social.href" target="_blank"
                            rel="noopener noreferrer" :aria-label="social.name"
                            class="flex size-9 items-center justify-center rounded-full bg-white/5 text-gray-400 ring-1 ring-white/10 transition-colors hover:bg-accent-500/10 hover:text-accent-400 hover:ring-accent-500/30">
                            <component :is="social.icon" class="size-4" aria-hidden="true" />
                        </a>
                    </div>
                </div>
            </div>

            <div class="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
                <p class="text-sm text-gray-500">&copy; {{ currentYear }} Global Arabic. All rights reserved.</p>
                <div class="flex gap-x-6">
                    <router-link to="/privacy-policy" class="text-sm text-gray-500 hover:text-white">Privacy Policy</router-link>
                    <router-link to="/contact-us" class="text-sm text-gray-500 hover:text-white">Contact Us</router-link>
                </div>
            </div>
        </div>
    </footer>
</template>

<script setup>
import { CalendarDaysIcon, HandRaisedIcon } from '@heroicons/vue/24/outline'
import { onMounted, ref } from 'vue';
import Button from '@/components/ui/Button.vue';
import { getFeaturedCategories } from '@/services/categoryService';

const categories = ref([]);
const currentYear = new Date().getFullYear();

const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about-us' },
    { name: 'Courses', href: '/courses' },
    { name: 'Books', href: '/books' },
    { name: 'Contact Us', href: '/contact-us' },
];

const socials = [
    {
        name: 'Facebook',
        href: 'https://www.facebook.com/people/Global-Arabic-Academy/61578348283287/',
        icon: {
            template: `<svg viewBox="0 0 71 72" fill="currentColor"><path d="M46.4233 38.6403L47.7279 30.3588H39.6917V24.9759C39.6917 22.7114 40.8137 20.4987 44.4013 20.4987H48.1063V13.4465C45.9486 13.1028 43.7685 12.9168 41.5834 12.8901C34.9692 12.8901 30.651 16.8626 30.651 24.0442V30.3588H23.3193V38.6403H30.651V58.671H39.6917V38.6403H46.4233Z"/></svg>`,
        },
    },
    {
        name: 'Instagram',
        href: 'https://www.instagram.com/global_academy5981/',
        icon: {
            template: `<svg viewBox="0 0 71 72" fill="currentColor"><path d="M27.3762 35.7808C27.3762 31.1786 31.1083 27.4468 35.7132 27.4468C40.3182 27.4468 44.0522 31.1786 44.0522 35.7808C44.0522 40.383 40.3182 44.1148 35.7132 44.1148C31.1083 44.1148 27.3762 40.383 27.3762 35.7808ZM22.8683 35.7808C22.8683 42.8708 28.619 48.618 35.7132 48.618C42.8075 48.618 48.5581 42.8708 48.5581 35.7808C48.5581 28.6908 42.8075 22.9436 35.7132 22.9436C28.619 22.9436 22.8683 28.6908 22.8683 35.7808ZM25.6072 56.1302C23.1683 56.0192 21.8427 55.6132 20.9618 55.2702C19.7939 54.8158 18.9606 54.2746 18.0845 53.4002C17.2083 52.5258 16.666 51.6938 16.2133 50.5266C15.8699 49.6466 15.4637 48.3214 15.3528 45.884C15.2316 43.2488 15.2073 42.4572 15.2073 35.781C15.2073 29.1048 15.2336 28.3154 15.3528 25.678C15.4639 23.2406 15.8731 21.918 16.2133 21.0354C16.668 19.8682 17.2095 19.0354 18.0845 18.1598C18.9594 17.2842 19.7919 16.7422 20.9618 16.2898C21.8423 15.9466 23.1683 15.5406 25.6072 15.4298C28.244 15.3086 29.036 15.2844 35.7132 15.2844C42.3904 15.2844 43.1833 15.3106 45.8223 15.4298C48.2612 15.5408 49.5846 15.9498 50.4677 16.2898C51.6356 16.7422 52.4689 17.2854 53.345 18.1598C54.2211 19.0342 54.7615 19.8682 55.2161 21.0354C55.5595 21.9154 55.9658 23.2406 56.0767 25.678C56.1979 28.3154 56.2221 29.1048 56.2221 35.781C56.2221 42.4572 56.1979 43.2466 56.0767 45.884C55.9656 48.3214 55.5573 49.6462 55.2161 50.5266C54.7615 51.6938 54.2199 52.5266 53.345 53.4002C52.4701 54.2738 51.6356 54.8158 50.4677 55.2702C49.5872 55.6134 48.2612 56.0194 45.8223 56.1302C43.1855 56.2514 42.3934 56.2756 35.7132 56.2756C29.033 56.2756 28.2432 56.2514 25.6072 56.1302ZM25.4001 10.9322C22.7371 11.0534 20.9174 11.4754 19.3282 12.0934C17.6824 12.7316 16.2892 13.5878 14.897 14.977C13.5047 16.3662 12.6502 17.7608 12.0116 19.4056C11.3933 20.9948 10.971 22.8124 10.8497 25.4738C10.7265 28.1394 10.6982 28.9916 10.6982 35.7808C10.6982 42.57 10.7265 43.4222 10.8497 46.0878C10.971 48.7494 11.3933 50.5668 12.0116 52.156C12.6502 53.7998 13.5049 55.196 14.897 56.5846C16.289 57.9732 17.6824 58.8282 19.3282 59.4682C20.9204 60.0862 22.7371 60.5082 25.4001 60.6294C28.0687 60.7506 28.92 60.7808 35.7132 60.7808C42.5065 60.7808 43.3592 60.7526 46.0264 60.6294C48.6896 60.5082 50.5081 60.0862 52.0983 59.4682C53.7431 58.8282 55.1373 57.9738 56.5295 56.5846C57.9218 55.1954 58.7745 53.7998 59.4149 52.156C60.0332 50.5668 60.4575 48.7492 60.5768 46.0878C60.698 43.4202 60.7262 42.57 60.7262 35.7808C60.7262 28.9916 60.698 28.1394 60.5768 25.4738C60.4555 22.8122 60.0332 20.9938 59.4149 19.4056C58.7745 17.7618 57.9196 16.3684 56.5295 14.977C55.1395 13.5856 53.7431 12.7316 52.1003 12.0934C50.5081 11.4754 48.6894 11.0514 46.0284 10.9322C43.3612 10.811 42.5085 10.7808 35.7152 10.7808C28.922 10.7808 28.0687 10.809 25.4001 10.9322Z"/></svg>`,
        },
    },
    {
        name: 'YouTube',
        href: 'https://www.youtube.com/@GlobalArabic-j2i',
        icon: {
            template: `<svg viewBox="0 0 71 71" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M56.5615 18.2428C58.8115 18.8504 60.58 20.6234 61.1778 22.8708C62.2654 26.9495 62.2654 35.4647 62.2654 35.4647C62.2654 35.4647 62.2654 43.98 61.1778 48.0586C60.5717 50.3144 58.8032 52.0873 56.5615 52.6866C52.4932 53.7771 36.1703 53.7771 36.1703 53.7771C36.1703 53.7771 19.8557 53.7771 15.7791 52.6866C13.5291 52.079 11.7606 50.306 11.1628 48.0586C10.0752 43.98 10.0752 35.4647 10.0752 35.4647C10.0752 35.4647 10.0752 26.9495 11.1628 22.8708C11.7689 20.615 13.5374 18.8421 15.7791 18.2428C19.8557 17.1523 36.1703 17.1523 36.1703 17.1523C36.1703 17.1523 52.4932 17.1523 56.5615 18.2428ZM44.5142 35.4647L30.9561 43.314V27.6154L44.5142 35.4647Z"/></svg>`,
        },
    },
];

onMounted(() => {
    getFeaturedCategories().then(data => {
        categories.value = data.data;
    }).catch(() => {
        // ignore
    });
});
</script>
